// The markdown editor (@mdxeditor/editor, Lexical-based) splits text nodes at
// UTF-16 *code unit* offsets, which can land in the middle of a surrogate pair
// and orphan half of it (a lone high or low surrogate). A lone surrogate has
// no UTF-8 representation, so when that string reaches the backend and
// psycopg tries to encode it for Postgres, the write fails with a raw
// UnicodeEncodeError and the request 500s. We repair every outgoing GraphQL
// variable before it leaves the client so a malformed string can never reach
// the server.

// Hermes (React Native's JS engine, used by `mobil` and `neon_flow`) may not
// implement the ES2024 `String.prototype.toWellFormed`/`isWellFormed`
// built-ins, so we fall back to a manual scan. Hermes's lookbehind support is
// unreliable, so the regex avoids it: it matches a valid surrogate pair as
// its own alternative first, so a valid pair survives untouched even when it
// sits right next to a lone surrogate.
const SURROGATE_REGEX = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g;

const toWellFormedTextFallback = (value: string): string =>
  value.replace(SURROGATE_REGEX, (match) => (match.length === 2 ? match : "�"));

/**
 * Returns `value` with any lone (unpaired) UTF-16 surrogate replaced by
 * U+FFFD. Returns the original reference, unchanged, when `value` is already
 * well-formed — which is the overwhelmingly common case.
 */
export const toWellFormedText = (value: string): string => {
  if (typeof value.isWellFormed === "function") {
    if (value.isWellFormed()) return value;
    return typeof value.toWellFormed === "function"
      ? value.toWellFormed()
      : toWellFormedTextFallback(value);
  }
  return toWellFormedTextFallback(value);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

/**
 * Deep-walks GraphQL operation variables and repairs any lone UTF-16
 * surrogate found in a string leaf.
 *
 * Only recurses into arrays and plain objects. Anything else — `File`,
 * `Blob`, `Date`, `ReactNativeFile`, or any other class instance — is
 * returned by reference, never cloned and never recursed into, because
 * `apollo-upload-client`'s `UploadHttpLink` extracts files out of `variables`
 * by identity.
 *
 * When nothing in the tree changed, the original reference is returned at
 * every level, so this is a true no-op in the normal (well-formed) case.
 */
export const toWellFormedVariables = <T>(variables: T): T => {
  if (variables === null || variables === undefined) return variables;

  if (typeof variables === "string") {
    return toWellFormedText(variables) as unknown as T;
  }

  if (Array.isArray(variables)) {
    let changed = false;
    const next = variables.map((item) => {
      const sanitized = toWellFormedVariables(item);
      if (sanitized !== item) changed = true;
      return sanitized;
    });
    return (changed ? next : variables) as unknown as T;
  }

  if (isPlainObject(variables)) {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(variables)) {
      const value = variables[key];
      const sanitized = toWellFormedVariables(value);
      if (sanitized !== value) changed = true;
      next[key] = sanitized;
    }
    return (changed ? next : variables) as unknown as T;
  }

  return variables;
};
